"use client";

import { motion, useReducedMotion } from "framer-motion";

const CYCLE = 22;

const INPUT_TIMES = [0, 0.06, 0.32, 0.40, 1.0] as const;
const INPUT_OPAC  = [0, 0.65, 0.65,  0,   0  ] as const;

const OUTPUT_TIMES = [0, 0.42, 0.50, 0.92, 1.0] as const;
const OUTPUT_OPAC  = [0, 0,    0.70, 0.70,  0  ] as const;

const SCAN_TIMES = [0, 0.30, 0.31, 0.48, 0.49, 1.0] as const;
const SCAN_OPAC  = [0, 0,    0.60, 0.60,  0,    0  ] as const;
const SCAN_X     = [-40, -40, -40, 260,  260,  260 ] as const;

interface GridLineDef {
  x1: number; y1: number; x2: number; y2: number;
  opacity: number; duration: number; delay: number;
}

interface DrupalMarkupDef {
  x: number; y: number; delay: number;
  raw: string[];
  clean: string[];
}

interface GovernanceDef {
  glyph: string; x: number; y: number;
  opacity: number; duration: number; delay: number;
}

const GRID_LINES: GridLineDef[] = [
  { x1: 52, y1: 0,  x2: 52, y2: 100, opacity: 0.04, duration: 30, delay: 0 },
  { x1: 68, y1: 0,  x2: 68, y2: 100, opacity: 0.035, duration: 34, delay: 2 },
  { x1: 84, y1: 0,  x2: 84, y2: 100, opacity: 0.03, duration: 28, delay: 4 },
  { x1: 48, y1: 20, x2: 100, y2: 20, opacity: 0.03, duration: 26, delay: 1 },
  { x1: 48, y1: 45, x2: 100, y2: 45, opacity: 0.035, duration: 32, delay: 3 },
  { x1: 48, y1: 70, x2: 100, y2: 70, opacity: 0.03, duration: 24, delay: 5 },
  { x1: 48, y1: 90, x2: 100, y2: 90, opacity: 0.025, duration: 36, delay: 7 },
  { x1: 60, y1: 0,  x2: 60, y2: 100, opacity: 0.025, duration: 38, delay: 6 },
  { x1: 76, y1: 0,  x2: 76, y2: 100, opacity: 0.03, duration: 30, delay: 8 },
  { x1: 92, y1: 0,  x2: 92, y2: 100, opacity: 0.025, duration: 32, delay: 1.5 },
];

const MARKUP_COLUMNS: DrupalMarkupDef[] = [
  {
    x: 52, y: 4, delay: 0,
    raw: [
      '<div class="layout--twocol">',
      '  <div class="layout__region--first">',
      '    <div data-block="field_body">',
      '    <div class="paragraph--type--text">',
    ],
    clean: [
      "# Enterprise content",
      "## Governed and discoverable",
      "- Structured output",
      "- Permission-aware publishing",
    ],
  },
  {
    x: 70, y: 28, delay: 5,
    raw: [
      '"jsonapi": { "version": "1.0" },',
      '"data": { "type": "node--article",',
      '  "attributes": { "field_body":',
      '    { "value": "<p>...</p>" } }',
    ],
    clean: [
      "## Structured metadata",
      "- Clean semantic output",
      "- Rich type hierarchies",
      "- AI visibility at scale",
    ],
  },
  {
    x: 55, y: 56, delay: 10,
    raw: [
      '<section data-region="content">',
      '  <article role="article" about=',
      '  <div class="node__content">',
      '  <div field="field_paragraphs">',
    ],
    clean: [
      "### Content architecture",
      "- Entity relationships mapped",
      "- Field-level permissions",
      "- Revision-safe structure",
    ],
  },
  {
    x: 76, y: 64, delay: 3,
    raw: [
      '"meta": { "drupal_internal__nid":',
      '"links": { "self": { "href":',
      '"relationships": { "uid": {',
      '"field_tags": { "data": [{ }] }',
    ],
    clean: [
      "## Token efficiency",
      "**Before:** ~6,800 tokens/page",
      "**After:** ~420 tokens/page",
      "Governed, cited, AI-native",
    ],
  },
];

const GOVERNANCE_CUES: GovernanceDef[] = [
  { glyph: "🛡",  x: 88, y: 12, opacity: 0.18, duration: 28, delay: 2 },
  { glyph: "¹",   x: 62, y: 22, opacity: 0.22, duration: 24, delay: 6 },
  { glyph: "→",   x: 78, y: 38, opacity: 0.15, duration: 30, delay: 10 },
  { glyph: "🛡",  x: 56, y: 82, opacity: 0.14, duration: 26, delay: 4 },
  { glyph: "¹",   x: 92, y: 52, opacity: 0.20, duration: 22, delay: 8 },
  { glyph: "→",   x: 66, y: 72, opacity: 0.16, duration: 32, delay: 12 },
  { glyph: "¹",   x: 82, y: 88, opacity: 0.13, duration: 34, delay: 1 },
  { glyph: "🛡",  x: 72, y: 8,  opacity: 0.10, duration: 36, delay: 14 },
];

function GridLayer({ reduced }: { reduced: boolean }) {
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        {GRID_LINES.map((line, i) => (
          <motion.line
            key={i}
            x1={`${line.x1}%`} y1={`${line.y1}%`}
            x2={`${line.x2}%`} y2={`${line.y2}%`}
            stroke="rgba(148,163,184,1)"
            strokeWidth="0.5"
            style={{ opacity: line.opacity }}
            animate={reduced ? undefined : {
              opacity: [line.opacity, line.opacity * 1.8, line.opacity],
            }}
            transition={reduced ? undefined : {
              duration: line.duration,
              delay: line.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </svg>
    </div>
  );
}

function CleanLine({ line }: { line: string }) {
  let cls = "text-blue-300/75";
  let shadow: string | undefined;

  if (line.startsWith("# ") || line.startsWith("## ") || line.startsWith("### ")) {
    cls = "text-blue-200/90 font-semibold";
    shadow = "0 0 10px rgba(96,165,250,0.30)";
  } else if (line.startsWith("- ")) {
    cls = "text-blue-400/75";
  } else if (line.startsWith("**") || line.includes("**")) {
    cls = "text-blue-100/80";
  } else if (line.includes("Governed") || line.includes("cited") || line.includes("AI-native")) {
    cls = "text-cyan-300/70";
  }

  return (
    <div
      className={`font-mono text-[10px] leading-[1.45] whitespace-nowrap ${cls}`}
      style={shadow ? { textShadow: shadow } : undefined}
    >
      {line}
    </div>
  );
}

function MarkupColumn({ col, reduced }: { col: DrupalMarkupDef; reduced: boolean }) {
  const { x, y, delay, raw, clean } = col;

  const makeTransition = (times: readonly number[]) => ({
    duration: CYCLE,
    times: [...times],
    delay,
    repeat: Infinity,
    ease: "easeInOut" as const,
  });

  if (reduced) {
    return (
      <div
        className="absolute pointer-events-none"
        style={{ left: `${x}%`, top: `${y}%`, width: 220 }}
      >
        <div className="bg-slate-950/50 border border-blue-500/20 rounded-md px-3 py-2 backdrop-blur-sm">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500/70" />
            <span className="font-mono text-[9px] text-blue-400/50 uppercase tracking-wider">structured</span>
          </div>
          {clean.map((line, i) => <CleanLine key={i} line={line} />)}
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute pointer-events-none"
      style={{ left: `${x}%`, top: `${y}%`, width: 220 }}
    >
      <div className="relative h-24 overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-slate-950/55 border border-red-800/25 rounded-md px-3 py-2 backdrop-blur-sm"
          animate={{ opacity: [...INPUT_OPAC] }}
          transition={makeTransition(INPUT_TIMES)}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-700/60" />
            <span className="font-mono text-[9px] text-red-400/40 uppercase tracking-wider">drupal raw</span>
          </div>
          {raw.map((line, i) => (
            <div key={i} className="font-mono text-[10px] leading-[1.45] whitespace-nowrap text-red-400/55">
              {line}
            </div>
          ))}
        </motion.div>

        <motion.div
          className="absolute inset-0 pointer-events-none rounded-md overflow-hidden"
          animate={{ opacity: [...SCAN_OPAC] }}
          transition={makeTransition(SCAN_TIMES)}
        >
          <motion.div
            className="absolute top-0 bottom-0 w-10"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(96,165,250,0.22) 40%, rgba(147,197,253,0.15) 60%, transparent)",
              boxShadow: "0 0 10px 2px rgba(96,165,250,0.08)",
            }}
            animate={{ x: [...SCAN_X] }}
            transition={makeTransition(SCAN_TIMES)}
          />
        </motion.div>

        <motion.div
          className="absolute inset-0 bg-slate-950/55 border border-blue-500/20 rounded-md px-3 py-2 backdrop-blur-sm"
          animate={{ opacity: [...OUTPUT_OPAC] }}
          transition={makeTransition(OUTPUT_TIMES)}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500/70" />
            <span className="font-mono text-[9px] text-blue-400/50 uppercase tracking-wider">structured</span>
          </div>
          {clean.map((line, i) => <CleanLine key={i} line={line} />)}
        </motion.div>
      </div>
    </div>
  );
}

function GovernanceLayer({ reduced }: { reduced: boolean }) {
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      {GOVERNANCE_CUES.map((cue, i) => (
        <motion.span
          key={i}
          className="absolute font-mono text-sm text-blue-200 select-none"
          style={{
            left: `${cue.x}%`,
            top: `${cue.y}%`,
            opacity: cue.opacity,
          }}
          animate={reduced ? undefined : {
            opacity: [cue.opacity * 0.4, cue.opacity, cue.opacity * 0.4],
          }}
          transition={reduced ? undefined : {
            duration: cue.duration,
            delay: cue.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {cue.glyph}
        </motion.span>
      ))}
    </div>
  );
}

export function DrupalAnimation() {
  const reduced = useReducedMotion() ?? false;

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none select-none overflow-hidden"
      style={{ zIndex: 0 }}
    >
      <GridLayer reduced={reduced} />

      {MARKUP_COLUMNS.map((col, i) => (
        <div
          key={i}
          className={
            i === 0
              ? "block"
              : i <= 2
              ? "hidden sm:block"
              : "hidden lg:block"
          }
        >
          <MarkupColumn col={col} reduced={reduced} />
        </div>
      ))}

      <div className="hidden sm:block">
        <GovernanceLayer reduced={reduced} />
      </div>
    </div>
  );
}

export default DrupalAnimation;
